import axios from 'axios';
import CloseIcon from '@mui/icons-material/Close';
import AssignmentIcon from '@mui/icons-material/Assignment';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Modal from '@mui/material/Modal';
import { styled } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import React from 'react';
import PropTypes from 'prop-types';

import CustomButton from '../CustomButton';
import { getFormErrors } from '../../modules/formErrors';
import { trackGoal } from '../../modules/matomo';
import schema from '../../schema/inquiry';

import Loading from '../Loading';

const Content = React.lazy(() => import('./Content'));

axios.defaults.baseURL = import.meta.env.VITE_API_BASE_URL;

const StyledModal = styled(Modal)(() => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

const ModalContent = styled(Box)(({ theme }) => ({
  overflow: 'auto',
  maxHeight: '90vh',
  position: 'relative',
  background: theme.palette.background.gradient,
  borderRadius: theme.shape.borderRadius,
  boxShadow: theme.shadows[5],
  padding: theme.spacing(4),
  width: '100%',
  maxWidth: '90%',
  outline: 'none',
  [theme.breakpoints.up('md')]: {
    maxWidth: '80vw',
    '.formContainer': {
      marginLeft: '36%',
    },
  },
  [theme.breakpoints.up('lg')]: {
    maxWidth: '64vw',
    '.formContainer': {
      marginLeft: '54%',
    },
  },
  [theme.breakpoints.up('xl')]: {
    maxWidth: '50vw',
  },
}));

const CloseButton = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  top: theme.spacing(1),
  right: theme.spacing(1),
  color: theme.palette.text.primary,
}));

const StyledAlert = styled(Alert)(({ theme }) => ({
  paddingTop: theme.spacing(2),
  fontSize: '1.5vw',
  '.MuiAlert-icon': {
    fontSize: '2.5vw',
  },
}));

const ProjectModal = ({ open, onClose, topic = '' }) => {
  const [sent, setSent] = React.useState(false);

  const handleSubmit = async (values, actions) => {
    try {
      await axios.post('/api/inquiry', values);
      actions.resetForm();
      setSent(true);
      trackGoal(1);
    } catch (e) {
      actions.setErrors(getFormErrors(
        e,
        Object.keys(schema.initialValues),
        'Die Anfrage konnte nicht gesendet werden. Bitte versuchen Sie es erneut.',
      ));
    } finally {
      actions.setSubmitting(false);
    }
  };

  const handleClose = () => {
    setSent(false);
    onClose();
  }
  
  return (
    <StyledModal
      open={open}
      onClose={handleClose}
    >
      <ModalContent
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-labelledby="project-modal-title"
        aria-describedby="project-modal-description"
      >
        <Tooltip title="Fenster schließen">
          <CloseButton onClick={handleClose} aria-label="schließen">
            <CloseIcon />
          </CloseButton>
        </Tooltip>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <AssignmentIcon color="brand" sx={{ fontSize: '2.5rem' }} />
          <Typography id="project-modal-title" variant="h4" component="h2" sx={{ fontFamily: 'Usuzi', fontWeight: 'bold' }} gutterBottom>
            Projekt anfragen
          </Typography>
        </Box>

        {sent ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, pt: 2 }}>
            <StyledAlert severity="success" icon={<CheckCircleOutlineIcon fontSize="inherit" />}>
              <strong>
                Vielen Dank für Ihre Anfrage!
              </strong>
              <p>
                Wir haben Ihre Projektanfrage erhalten und melden uns zeitnah bei Ihnen.
              </p>
            </StyledAlert>
            <CustomButton
              fullWidth
              onClick={handleClose}
              sx={{ mt: 1 }}
            >
              Schließen
            </CustomButton>
          </Box>
        ) : (
            <React.Suspense fallback={<Loading />}>
              <Content handleClose={handleClose} handleSubmit={handleSubmit} topic={topic} />
            </React.Suspense>
        )}

      </ModalContent>
    </StyledModal>
  );
};

ProjectModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  topic: PropTypes.string,
};

export default ProjectModal;
